import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { bool, int, useQueryParams } from '../../hooks/useQueryParams';
import { dateTime, eur, fullName, usd } from '../../services/format';
import { Alert, Badge, Button, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table } from '../../components/ui';
import { DISCOVERY_SOURCES } from '../../types/api';

const PAGE_SIZE = 50;

export function UsersList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const q = get('q');
  const verified = get('verified');
  const staff = get('staff');
  const organizationId = get('organization_id');
  const discovery = get('discovery_source');
  const { data, error, loading } = useAsync(
    () => api.users({ q: q || undefined, verified: bool(verified), is_staff: bool(staff), organization_id: int(organizationId), discovery_source: discovery || undefined, page, page_size: PAGE_SIZE }),
    [q, verified, staff, organizationId, discovery, page],
  );

  return (
    <div>
      <PageTitle title={t('users.title')} subtitle={data ? t('common.count', { count: data.total }) : undefined} actions={<Button variant="primary" onClick={() => navigate('/users/new')}>{t('users.new')}</Button>} />
      <Filters onReset={reset}>
        <Field label={t('common.search')} className="w-64">
          <Input value={q} onChange={(e) => set({ q: e.target.value })} placeholder={t('users.searchPlaceholder')} data-testid="users-search" />
        </Field>
        <Field label={t('users.verified')}>
          <Select value={verified} onChange={(e) => set({ verified: e.target.value })}>
            <option value="">{t('common.any')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </Select>
        </Field>
        <Field label={t('users.staff')}>
          <Select value={staff} onChange={(e) => set({ staff: e.target.value })}>
            <option value="">{t('common.any')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </Select>
        </Field>
        <Field label={t('users.organizationId')} className="w-32">
          <Input type="number" value={organizationId} onChange={(e) => set({ organization_id: e.target.value })} />
        </Field>
        <Field label={t('users.discovery')}>
          <Select value={discovery} onChange={(e) => set({ discovery_source: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {DISCOVERY_SOURCES.map((s) => (
              <option key={s} value={s}>{t(`users.discoverySource.${s}`)}</option>
            ))}
          </Select>
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table head={[t('users.email'), t('users.name'), t('users.organization'), t('users.verified'), t('users.active'), t('users.staff'), t('users.spend'), t('users.cost'), t('users.lastConnection')]} empty={loading ? t('common.loading') : t('common.empty')}>
        {(data?.items ?? []).map((u) => (
          <Row key={u.id} onClick={() => navigate(`/users/${u.id}`)}>
            <Cell mono>{u.email}</Cell>
            <Cell>{fullName(u.first_name, u.last_name, '—')}</Cell>
            <Cell>
              {u.organization ? (
                <>
                  {u.organization.name} <span className="font-mono text-[10.5px] text-faint">· {t(`orgs.role.${u.organization.role}`)}</span>
                </>
              ) : (
                '—'
              )}
            </Cell>
            <Cell>{u.email_verified ? <Badge tone="good">{t('common.yes')}</Badge> : <Badge tone="warn">{t('common.no')}</Badge>}</Cell>
            <Cell>{u.is_active ? <Badge tone="good">{t('common.yes')}</Badge> : <Badge tone="bad">{t('common.no')}</Badge>}</Cell>
            <Cell>{u.is_staff ? <Badge tone="accent">{t('users.staff')}</Badge> : ''}</Cell>
            <Cell mono>{eur(u.billed_eur, i18n.language)}</Cell>
            <Cell mono>{usd(u.cost_usd, i18n.language)}</Cell>
            <Cell mono>{dateTime(u.last_connection, i18n.language)}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
